import { LocalStorageKeys } from './../../shared/models/constants/storage.keys';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { CarouselPage } from 'src/app/shared/models/carousel/carousel-page.model';
import { Router } from '@angular/router';
import { trigger, style, animate, transition } from '@angular/animations';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { ViewportScroller } from '@angular/common';
import { RoutesKeys } from 'src/app/utils/routes';
import { Subscription, take } from 'rxjs';
import { RegisterService } from 'src/app/core/services/register/register.service';
import { UserService } from 'src/app/core/api/user/user.service';
import { ProductService } from 'src/app/core/api/product/product.service';
import { RewardService } from 'src/app/core/api/reward/reward.service';

@Component({
  templateUrl: './onboarding.page.html',
  styleUrls: ['./onboarding.page.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fadeInOut', [
      transition(':leave', [animate(1500, style({ opacity: 0 }))]),
    ]),
  ],
})
/**
 * Instantiates Onboarding template with information
 */
export class OnboardingPage implements OnInit, OnDestroy {
  RouterKeys = RoutesKeys; // Make available to HTML template
  showContainer: boolean = false;
  showSplash: boolean = true;
  nextPageLabel: string = 'next';
  previousPageLabel: string = 'previous';
  finishLabel: string = 'goto-login';
  anchorText: string = 'skip-to-login';
  carouselPages: CarouselPage[] = [
    new CarouselPage('event', 'onboarding-step-1'),
    new CarouselPage('star', 'onboarding-step-2'),
    new CarouselPage('groups', 'onboarding-step-3'),
  ];
  termsAndConditionsLink?: string;
  privacyPolicyLink?: string;
  private subsink = new Subscription();

  customOptions: OwlOptions = {
    loop: false,
    mouseDrag: true,
    touchDrag: true,
    pullDrag: true,
    dots: true,
    navSpeed: 700,
    navText: ['', ''],
    responsive: {
      0: {
        items: 1,
      },
      620: {
        items: 1,
      },
      740: {
        items: 2,
      },
      1250: {
        items: 3,
      },
    },
    nav: false,
    stagePadding: 50,
    margin: 25,
  };
  /**
   * Injects translocoService to be used inside html as a directive,
   * the Angular Breakpoint Observer, and the Scroller
   * @constructor
   */
  constructor(
    private translocoService: TranslocoService,
    private registerService: RegisterService,
    private readonly userService: UserService,
    private readonly productService: ProductService,
    private readonly rewardService: RewardService,
    private breakpointObserver: BreakpointObserver,
    private viewportScroller: ViewportScroller,
    private router: Router
  ) {}

  /**
   * Sets up translations for temporary pages object
   */
  ngOnInit(): void {
    this.userService
      .getApiVersion()
      .subscribe((res: string) => console.log(`User API version: ${res}`));
    this.productService
      .getApiVersion()
      .subscribe((res: string) => console.log(`Product API version: ${res}`));
    this.rewardService
      .getApiVersion()
      .subscribe((res: string) => console.log(`Reward API version: ${res}`));

    this.subsink.add(
      this.breakpointObserver
        .observe(['(min-width: 1181px)'])
        .subscribe((state: BreakpointState) => {
          if (state.matches) {
            this.showContainer = true;
          } else {
            this.showContainer = false;
          }
        })
    );

    for (const page of this.carouselPages) {
      this.subsink.add(
        this.translocoService
          .selectTranslate(page.text)
          .subscribe((translation) => {
            page.text = translation;
          })
      );
    }

    if (localStorage.getItem(LocalStorageKeys.ONBOARD)) {
      this.showSplash = false;
    }

    setTimeout(() => {
      this.showSplash = false;
    }, 2000);

    this.registerService
      .getTermsOfUseAndPrivacyConditionsURLs()
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          this.termsAndConditionsLink = res.termsOfUseURL;
          this.privacyPolicyLink = res.privacyConditionsURL;
        },
      });
  }

  /**
   * Cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Set local storage after the view initiaded
   */
  ngAfterViewInit() {
    localStorage.setItem(LocalStorageKeys.ONBOARD, 'true');
  }

  /**
   * Routes user to login page
   */
  goToLogin() {
    this.router.navigate([RoutesKeys.LOGIN]);
  }

  /**
   *  Scrolls to anchor
   * @param {string} elementId elementId of the screen
   */
  onClick(elementId: string): void {
    this.viewportScroller.scrollToAnchor(elementId);
  }
}
